import { ApiError } from "@/lib/apiClient";
import { Nullable } from "@/types";
import { Category } from "@/types/models/category";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

type wholesalerCollectionState = {
  byWholesalerId: {
    [id: string]: {
      data: Category[];
      loading: boolean;
      error: Nullable<ApiError>;
      pagination: {
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      };
    };
  };
  selectedCollectionIds: string[];
};
const initialState: wholesalerCollectionState = {
  byWholesalerId: {} as {
    [id: string]: {
      data: Category[];
      loading: boolean;
      error: Nullable<ApiError>;
      pagination: {
        page: number;
        total: number;
        pageSize: number;
        totalPages: number;
      };
    };
  },
  selectedCollectionIds: [],
};

const wholesalerCollectionSlice = createSlice({
  name: "wholesalerCollections",
  initialState,
  reducers: {
    fetchWholesalerCollectionRequest: (
      state,
      action: PayloadAction<{ id: string; page: number; pageSize: number }>,
    ) => {
      state.byWholesalerId[action.payload.id] = {
        loading: true,
        error: null,
        data: [],
        pagination: {
          page: action.payload.page,
          total: 0,
          pageSize: action.payload.pageSize,
          totalPages: 0,
        },
      };
    },
    fetchWholesalerCollectionSuccess: (
      state,
      action: PayloadAction<{
        id: string;
        data: Category[];
        pagination: {
          page: number;
          total: number;
          pageSize: number;
          totalPages: number;
        };
      }>,
    ) => {
      state.byWholesalerId[action.payload.id] = {
        data: action.payload.data,
        loading: false,
        error: null,
        pagination: {
          page: action.payload.pagination.page,
          total: action.payload.pagination.total,
          pageSize: action.payload.pagination.pageSize,
          totalPages: action.payload.pagination.totalPages,
        },
      };
    },
    fetchWholesalerCollectionFailure: (
      state,
      action: PayloadAction<{ id: string; error: ApiError }>,
    ) => {
      state.byWholesalerId[action.payload.id].error = action.payload.error;
      state.byWholesalerId[action.payload.id].loading = false;
    },
    // attach wholesaler collection
    addWholesalerCollectionRequest: (
      state,
      action: PayloadAction<{
        id: string;
        margin: number;
        margin_type: string;
        payment_type: string[];
        collection_ids: string[];
      }>,
    ) => {
      const entry = state.byWholesalerId[action.payload.id];
      if (!entry) return;
      entry.loading = true;
      entry.error = null;
    },
    addWholesalerCollectionSuccess: (
      state,
      action: PayloadAction<{
        id: string;
        data: string[];
      }>,
    ) => {
      const collections = state.byWholesalerId[action.payload.id];
      if (!collections) return;
      const data = collections.data.map((item) => {
        if (!item.id) return item;
        return action.payload.data.includes(item.id)
          ? { ...item, already_mapped: true }
          : item;
      });
      collections.data = data;
      collections.loading = false;
      collections.error = null;
    },
    addWholesalerCollectionFailure: (
      state,
      action: PayloadAction<{ id: string; error: ApiError }>,
    ) => {
      state.byWholesalerId[action.payload.id].error = action.payload.error;
      state.byWholesalerId[action.payload.id].loading = false;
    },

    // detach wholesaler collection
    detachWholesalerCollectionRequest: (
      state,
      action: PayloadAction<{ id: string; collection_ids: string[] }>,
    ) => {
      const entry = state.byWholesalerId[action.payload.id];
      if (!entry) return;
      entry.loading = true;
      entry.error = null;
    },
    detachWholesalerCollectionSuccess: (
      state,
      action: PayloadAction<{ id: string; data: string[] }>,
    ) => {
      const collections = state.byWholesalerId[action.payload.id];
      if (!collections) return;
      const data = collections.data.map((item) => {
        if (!item.id) return item;
        return action.payload.data.includes(item.id)
          ? { ...item, already_mapped: false }
          : item;
      });
      collections.data = data;
      collections.loading = false;
      collections.error = null;
    },
    detachWholesalerCollectionFailure: (
      state,
      action: PayloadAction<{ id: string; error: ApiError }>,
    ) => {
      state.byWholesalerId[action.payload.id].error = action.payload.error;
      state.byWholesalerId[action.payload.id].loading = false;
    },
  },
});

export const {
  addWholesalerCollectionFailure,
  addWholesalerCollectionRequest,
  addWholesalerCollectionSuccess,
  fetchWholesalerCollectionFailure,
  fetchWholesalerCollectionRequest,
  fetchWholesalerCollectionSuccess,
  detachWholesalerCollectionFailure,
  detachWholesalerCollectionRequest,
  detachWholesalerCollectionSuccess,
} = wholesalerCollectionSlice.actions;

export default wholesalerCollectionSlice.reducer;
